/**
 * SavCreateModal — Ouverture d'un dossier SAV depuis un véhicule livré.
 * Vision : 2 clics max, formulaire ultra simple (motif · description · responsable).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Wrench, Zap, Car, Shield, FileText, Package, HelpCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = [
  { key: "mecanique",     label: "Mécanique",     Icon: Wrench },
  { key: "electronique",  label: "Électronique",  Icon: Zap },
  { key: "carrosserie",   label: "Carrosserie",   Icon: Car },
  { key: "garantie",      label: "Garantie",      Icon: Shield },
  { key: "administratif", label: "Administratif", Icon: FileText },
  { key: "accessoire",    label: "Accessoire",    Icon: Package },
  { key: "autre",         label: "Autre",         Icon: HelpCircle },
];

const SavCreateModal = ({ dossierId, onClose, onCreated }) => {
  const navigate = useNavigate();
  const [category, setCategory] = useState("mecanique");
  const [description, setDescription] = useState("");
  const [responsibleId, setResponsibleId] = useState("");
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.listAgencyUsers?.()
      .then((u) => alive && setUsers(u?.users || u || []))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      setError("Une description est requise.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.createSavForDossier(dossierId, {
        category,
        short_description: description.trim(),
        responsible_user_id: responsibleId || null,
      });
      onCreated?.(r);
      onClose?.();
      // Redirige vers la page dédiée du nouveau dossier SAV
      if (r?.case?.id) {
        navigate(`/app/sav/${r.case.id}`);
      }
    } catch (err) {
      setError(err.message || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="sav-create-modal"
      onClick={onClose}
    >
      <div
        className="wyvio-card w-full max-w-lg p-6 sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#111111]">
              Ouvrir un dossier SAV
            </h3>
            <p className="mt-1 text-[12px] text-[#6B7280]">
              Motif · description · responsable · c&apos;est tout.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="sav-modal-close"
            className="h-8 w-8 inline-flex items-center justify-center rounded-full text-[#9CA3AF] hover:text-[#111] hover:bg-[#F7F8FA]"
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Motif */}
          <div>
            <label className="text-[11.5px] font-medium text-[#6B7280] uppercase tracking-wider">
              Motif
            </label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {CATEGORIES.map((c) => {
                const active = category === c.key;
                const Icon = c.Icon;
                return (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`sav-category-${c.key}`}
                    onClick={() => setCategory(c.key)}
                    className={[
                      "inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-medium transition",
                      active
                        ? "bg-[#111] text-white"
                        : "bg-white border border-[#EEF2F5] text-[#6B7280] hover:text-[#111]",
                    ].join(" ")}
                  >
                    <Icon size={12} strokeWidth={2} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-[11.5px] font-medium text-[#6B7280] uppercase tracking-wider">
              Description courte
            </label>
            <textarea
              data-testid="sav-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={800}
              autoFocus
              placeholder="Ex. Bruit anormal à l'accélération sur les 500 premiers km."
              className="mt-2 w-full rounded-2xl border border-[#EEF2F5] bg-white p-3 text-[13px] text-[#111] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#C8FF2F]/60 resize-none"
            />
            <p className="mt-1 text-[10.5px] text-[#9CA3AF]">
              {description.length}/800
            </p>
          </div>

          {/* Responsable */}
          <div>
            <label className="text-[11.5px] font-medium text-[#6B7280] uppercase tracking-wider">
              Responsable
            </label>
            <select
              data-testid="sav-responsible"
              value={responsibleId}
              onChange={(e) => setResponsibleId(e.target.value)}
              className="mt-2 w-full h-10 rounded-full border border-[#EEF2F5] bg-white px-4 text-[13px] text-[#111] focus:outline-none focus:ring-2 focus:ring-[#C8FF2F]/60"
            >
              <option value="">— Personne assignée pour l&apos;instant —</option>
              {users.map((u) => (
                <option key={u.id || u._id} value={u.id || u._id}>
                  {u.name} {u.role ? `· ${u.role}` : ""}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p className="text-[12px] text-[#B91C1C]" data-testid="sav-create-error">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-full text-[12.5px] font-medium text-[#6B7280] hover:text-[#111]"
            >
              Annuler
            </button>
            <button
              type="submit"
              data-testid="sav-create-submit"
              disabled={submitting || !description.trim()}
              className="h-10 px-5 rounded-full inline-flex items-center gap-2 bg-[#C8FF2F] text-[#111] text-[12.5px] font-semibold hover:brightness-95 transition disabled:opacity-50"
            >
              {submitting && <Loader2 size={13} className="animate-spin" strokeWidth={2} />}
              Créer le dossier SAV
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SavCreateModal;
